import { useQuery } from '@tanstack/react-query';
import { useContext } from 'react';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { DataRequest } from 'hooks/useDataRequest';
import { ApiResponse } from 'services/api.service';
import { getErrorMessage } from 'utils/getErrorMessage';

export interface DataServiceQueryResult<DataType> {
  data: DataType | null;
  error: string | null;
  isError: boolean;
  isLoading: boolean;
}

export function useDataServiceQueryResult<DataType, ResponseData = DataType>(
  requestData: DataRequest,
  fetchDataFn: (dataRequest: DataRequest) => Promise<ApiResponse<ResponseData>>,
  mapResponseToData: (data: NonNullable<ResponseData>, requestData: DataRequest) => DataType,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalDependencies: any[] = []
): DataServiceQueryResult<DataType> {
  const { accountNumber } = useContext(TenantConfigContext);

  const query = useQuery({
    gcTime: 0,
    queryKey: ['data-service-query-result', accountNumber, requestData, additionalDependencies],
    queryFn: async (): Promise<DataType | null> => {
      const { data, error } = await fetchDataFn(requestData);

      if (error) {
        throw Error(error.message);
      }

      return data ? mapResponseToData(data, requestData) : null;
    },
    staleTime: 0,
  });

  return {
    data: query.data ?? null,
    error: query.isError ? getErrorMessage(query.error) : null,
    isError: query.isError,
    isLoading: query.isPending,
  };
}
